import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  DraggableResourceItem,
} from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/GoalPrioritization/components/DragDropPrioritization/components/DraggableResourceItem';
import { FDStack } from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PanicError from 'Errors/Panic';
import { v4 as uuid } from 'uuid';

const useStyles = buildUseStyles('DragDropPrioritization', () => ({
  root: {},
  fullWidth: {
    width: '100%',
  },
}));

/**
 * DragDropPrioritization component provides drag and drop functionality
 * for prioritizing a list of collection. Mobile-friendly with touch support.
 */
export function DragDropPrioritization({
  dropZoneId,
  items,
  onChange,
  onBreakGroup,
  ItemComponent,
  disabled = false,
  combineEnabled = false,
  ItemComponentProps,
}) {
  const classes = useStyles();

  /**
   * Groups an array of items by their 'groupUUID' attribute.
   * Returns an array of arrays. Each inner array is either a group of items or a single, ungrouped item.
   *
   * @param {Array} ungroupedItems - The array of resource items to group.
   * @returns {Array<Array>} An array of arrays, where each inner array is a group or a single item.
   */
  function groupItemsByUuid(ungroupedItems) {
    const finalGroups = [];
    const processedIds = new Set();

    for (const currentItem of ungroupedItems) {
      // If we've already processed this item (as part of a group), skip it.
      if (processedIds.has(currentItem.id())) {
        continue;
      }

      const groupUUID = currentItem.attr('groupUUID');

      if (groupUUID) {
        // If the item is part of a group, find all other ungroupedItems in that group.
        const group = ungroupedItems.filter(item => item.attr('groupUUID') === groupUUID);

        // Add all ungroupedItems from this group to the processed set to avoid processing them again.
        group.forEach(item => processedIds.add(item.id()));

        // Add the entire group as a single array to the final result.
        finalGroups.push(group);
      } else {
        // If the item is not in a group, it forms a group of one.
        const singleItemArray = [currentItem];
        finalGroups.push(singleItemArray);

        // Add the single item to the processed set.
        processedIds.add(currentItem.id());
      }
    }

    return finalGroups;
  }

  // We must have a dropZoneId to enable drag and drop
  if (!dropZoneId) {
    throw new PanicError('missing-required-error-parameter', 'DragDropPrioritization: dropZoneId is required');
  }

  const combineItems = (updatedItems, sourceDraggableId, combine) => {
    if (!combineEnabled) {
      return;
    }
    // Find the source and combined resources by their unique IDs.
    const sourceResource = updatedItems.find(item => item.id() === sourceDraggableId);
    const combinedResource = updatedItems.find(item => item.id() === combine.draggableId);

    // If there is no source or combined resource, we cannot combine.
    if (!sourceResource || !combinedResource) {
      return;
    }

    // Check if the combined resource is already part of a group
    const existinggroupUUID = combinedResource.attr('groupUUID');
    if (existinggroupUUID) {
      // Case A: The combined item is already in a group.
      // Apply the existing group's UUID to the source item.
      sourceResource.attr('groupUUID', existinggroupUUID);
    } else {
      // Case B: This is a brand new group of two items.
      // Create a new UUID and apply it to both items.
      const newgroupUUID = uuid();
      sourceResource.attr('groupUUID', newgroupUUID);
      combinedResource.attr('groupUUID', newgroupUUID);
    }
  };

  /**
   * Handles the drag end event and reorders the item list
   * @param {Object} result - The drag result from react-beautiful-dnd
   */
  const handleDragEnd = ({
    draggableId: sourceDraggableId, source, destination, combine,
  }) => {
    // Convert the current items into the grouped format
    const currentGroups = groupItemsByUuid(items);

    // 1. Handle the combination case first
    if (combine) {
      const updatedItems = Array.from(items); // Need flat array for combining
      combineItems(updatedItems, sourceDraggableId, combine);

      // Since combine mutates items in place, we pass the updated flat list back.
      if (onChange) {
        onChange(updatedItems);
      }
      return;
    }

    // 2. Handle the standard reordering case (if no combine occurred)
    if (!destination || source.index === destination.index) {
      return;
    }

    // Clone the groups array so we can safely mutate it
    const newGroups = Array.from(currentGroups);

    // Splice the group arrays based on the index supplied by react-beautiful-dnd.
    // NOTE: The element being moved is a group array (e.g., [{...}, {...}])
    const [reorderedGroup] = newGroups.splice(source.index, 1);
    newGroups.splice(destination.index, 0, reorderedGroup);

    // 3. Flatten the reordered groups back into a single item array
    const finalFlatItems = newGroups.flat();

    if (onChange) {
      onChange(finalFlatItems);
    }
  };

  /**
   * Handles the click of up/down buttons and reorders the list
   * @param {number} itemId - The id of the item to move
   * @param {number} direction - 1 for down -1 for up
   */
  const handleReorderClick = (itemId, direction) => {
    // 1. Get the groupUUID of the item that was clicked.
    const clickedItem = items.find(item => item.id() === itemId);
    if (!clickedItem) {
      return;
    }
    const clickedGroupUUID = clickedItem.attr('groupUUID');

    // 2. Create the current array of groups.
    const currentGroups = groupItemsByUuid(items);

    // 3. Find the index of the group that contains the clicked item.
    // The loop iterates over the 'groups' (which are arrays of items)
    let currentGroupIndex = -1;
    for (let i = 0; i < currentGroups.length; i++) {
      const group = currentGroups[i];

      // If the item has a UUID, we check the first item in the group array
      if (clickedGroupUUID && group[0].attr('groupUUID') === clickedGroupUUID) {
        currentGroupIndex = i;
        break;
      }
      // If the item is ungrouped (group.length === 1), we check the item ID
      if (!clickedGroupUUID && group.length === 1 && group[0].id() === itemId) {
        currentGroupIndex = i;
        break;
      }
    }

    if (currentGroupIndex === -1) {
      return; // Should not happen if data is valid
    }

    // 4. Calculate the new index for the group.
    const newGroupIndex = currentGroupIndex + direction;

    // 5. Ensure the new index is within bounds of the groups array.
    if (newGroupIndex < 0 || newGroupIndex >= currentGroups.length) {
      return;
    }

    // 6. Perform the splice operation on the ARRAY OF GROUPS.
    const newGroups = Array.from(currentGroups);
    const [reorderedGroup] = newGroups.splice(currentGroupIndex, 1); // Extract the group
    newGroups.splice(newGroupIndex, 0, reorderedGroup); // Insert the group

    // 7. Flatten the reordered groups back into the final flat item array.
    const finalFlatItems = newGroups.flat();

    if (onChange) {
      onChange(finalFlatItems);
    }
  };

  /**
   * Clears the groupUUID for all items belonging to the same group as the specified item.
   *
   * @param {string} itemId - The ID of an item within the group to be broken.
   */
  const handleBreakGroup = (itemId) => {
    if (!combineEnabled) {
      return;
    }
    // Create a new array to work with, leaving the original array untouched.
    const updatedItems = Array.from(items);

    // Find the item whose group we want to break.
    const itemInGroup = updatedItems.find(item => item.id() === itemId);

    // If the item or its groupUUID is not found, there's nothing to do.
    if (!itemInGroup || !itemInGroup.attr('groupUUID')) {
      return;
    }

    // Get the group's UUID to find all members of the group.
    const groupUUIDToBreak = itemInGroup.attr('groupUUID');

    // Iterate through the copied array and clear the groupUUID for all matching items.
    updatedItems.forEach(item => {
      if (item.attr('groupUUID') === groupUUIDToBreak) {
        // We assume your resource object has a way to set or clear attributes.
        // Using `attr(key, value)` is a common pattern.
        item.attr('groupUUID', null);
      }
    });

    if (onBreakGroup) {
      onBreakGroup(updatedItems);
    }
  };

  if (!items.length) {
    return null;
  }

  const groupedItems = groupItemsByUuid(items).filter(resourceGroup => resourceGroup.length > 0);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        className={classes.fullWidth}
        droppableId={dropZoneId}
        isDropDisabled={disabled}
        isCombineEnabled={combineEnabled}
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            className={classes.fullWidth}
            {...provided.droppableProps}
          >
            <FDStack direction={'column'} spacing={2} width={'100%'}>
              {groupedItems.map((resourceGroup, index) => (
                <DraggableResourceItem
                  key={resourceGroup[0].id()}
                  resourceGroup={resourceGroup}
                  index={index}
                  Component={ItemComponent}
                  ComponentProps={{
                    ...ItemComponentProps,
                    combineEnabled,
                    isFirst: index === 0,
                    isLast: index === groupedItems.length - 1,
                    onReorderClick: handleReorderClick,
                    onBreakGroup: handleBreakGroup,
                  }}
                  disabled={disabled}
                />
              ))}
              {provided.placeholder}
            </FDStack>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

DragDropPrioritization.propTypes = {
  /** Unique identifier for the drop zone */
  dropZoneId: PropTypes.string.isRequired,
  /** Array of items to prioritize */
  items: PropTypes.array.isRequired,
  /** Callback function called when items are reordered */
  onChange: PropTypes.func,
  /** Callback function called when an item group is broken */
  onBreakGroup: PropTypes.func,
  /** ItemComponent component to render the item details */
  ItemComponent: PropTypes.func.isRequired,
  /** Disabld sorting */
  disabled: PropTypes.bool,
  /** Combine enabled */
  combineEnabled: PropTypes.bool,
  /** Props to pass to the layout component */
  ItemComponentProps: PropTypes.object,
};
